import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useI18n } from "../lib/i18n";

const LANGS = [
  { value: "en", label: "English", short: "E" },
  { value: "hi", label: "हिंदी",    short: "हि" },
  { value: "mr", label: "मराठी",    short: "म" },
  { value: "pa", label: "ਪੰਜਾਬੀ",   short: "ਪੰ" },
  { value: "kn", label: "ಕನ್ನಡ",    short: "ಕ" },
  { value: "gu", label: "ગુજરાતી", short: "ગ" },
];

function LangSelect({ lang, setLang }) {
  return (
    <select
      value={lang}
      onChange={(e) => setLang(e.target.value)}
      aria-label="Select language"
      style={{
        background: "#1A1A1A",
        border: "1px solid rgba(245,245,245,0.1)",
        borderRadius: 6,
        color: "#F97316",
        fontSize: "0.82rem",
        fontWeight: 600,
        padding: "4px 24px 4px 8px",
        cursor: "pointer",
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236B7280'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 7px center",
        minWidth: 80,
      }}
    >
      {LANGS.map((l) => (
        <option key={l.value} value={l.value} style={{ background: "#1A1A1A", color: "#F5F5F5" }}>
          {l.short} · {l.label}
        </option>
      ))}
    </select>
  );
}

export default function Header({ sticky = false }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, lang, setLang } = useI18n();

  const navLinks = [
    { labelKey: "nav_for_tenants", href: "/for-tenants" },
    { labelKey: "nav_for_landlords", href: "/for-landlords" },
    { labelKey: "nav_for_brokers", href: "/for-brokers" },
    { labelKey: "nav_trust", href: "/trust" },
    { labelKey: "nav_about", href: "/about" },
    { labelKey: "nav_contact", href: "/contact" },
    { labelKey: "nav_login", href: "/login" },
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <>
      <nav
        className={`${sticky ? "sticky" : "relative"} top-0 z-50 w-full`}
        style={{
          background: "#080808",
          borderBottom: "1px solid rgba(245,245,245,0.06)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-3 flex items-center justify-between gap-4">
          {/* Logo */}
          <Logo size="sm" forceDark />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5 text-sm flex-1 justify-center flex-wrap">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                style={{
                  color: isActive(link.href) ? "#F97316" : "rgba(245,245,245,0.5)",
                  textDecoration: isActive(link.href) ? "underline" : "none",
                  transition: "color 0.2s ease",
                  whiteSpace: "nowrap",
                }}
                className="hover:text-white"
              >
                {t(link.labelKey)}
              </Link>
            ))}
          </div>

          {/* Language Dropdown */}
          <div className="hidden md:block" style={{ flexShrink: 0 }}>
            <LangSelect lang={lang} setLang={setLang} />
          </div>

          {/* Desktop CTA Button */}
          <Link
            to="/register"
            className="hidden md:inline-flex btn-press text-sm font-medium text-white px-5 py-2.5 rounded-full"
            style={{
              background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
              boxShadow: "0 4px 16px rgba(139,92,246,0.2)",
            }}
          >
            {t("nav_get_started")}
          </Link>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ color: "#F5F5F5" }}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="md:hidden border-t"
            style={{
              background: "#080808",
              borderColor: "rgba(245,245,245,0.06)",
            }}
          >
            <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: "block",
                    color: isActive(link.href) ? "#F97316" : "rgba(245,245,245,0.7)",
                    textDecoration: isActive(link.href) ? "underline" : "none",
                    padding: "0.75rem 0",
                    fontSize: "0.95rem",
                    transition: "color 0.2s ease",
                  }}
                  className="hover:text-white"
                >
                  {t(link.labelKey)}
                </Link>
              ))}
              <div style={{ paddingTop: "0.5rem", paddingBottom: "0.5rem" }}>
                <LangSelect lang={lang} setLang={setLang} />
              </div>

              <div style={{ paddingTop: "1rem" }}>
                <Link
                  to="/register"
                  onClick={() => setMobileMenuOpen(false)}
                  className="btn-press inline-flex w-full justify-center text-sm font-medium text-white px-5 py-2.5 rounded-full"
                  style={{
                    background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                    boxShadow: "0 4px 16px rgba(139,92,246,0.2)",
                  }}
                >
                  {t("nav_get_started")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
